import createChildWindow from "@core-ui/ChildWindow/createChildWindow";
import type GithubSourceAPI from "@ext/git/actions/Source/GitHub/logic/GithubSourceAPI";
import type GithubInstallation from "@ext/git/actions/Source/GitHub/model/GithubInstallation";
import { useRef, useState } from "react";

export type GithubInstallationOption = GithubInstallation & {
	label: string;
	value: string;
};

const useGithubInstallations = (sourceApi: GithubSourceAPI, isRelease: boolean) => {
	const [options, setOptions] = useState<GithubInstallationOption[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const isLoaded = useRef(false);
	// GitHub redirects after an installation to the app's fixed Setup URL, never to the desktop callback
	// server, so on desktop the child-window callback does not fire and the list is reloaded on open instead.
	const isAddingAccount = useRef(false);
	// Bumped when the caller should rebuild its repository list to show what was just granted to the GitHub app:
	// once on the first reload after «Add account» (the child-window callback or, on desktop, the next open),
	// since GitHub may only have granted repositories to an installation already listed, and afterwards
	// whenever a reload brings a different set of installations. A reopen with nothing new keeps the key,
	// so the repository list is not refetched on every open.
	const [reloadKey, setReloadKey] = useState(0);
	const isReloadPending = useRef(false);
	const installationNames = useRef<string | null>(null);

	const loadInstallations = (isCallback = false) => {
		setIsLoading(true);
		void sourceApi.getInstallations().then((installations) => {
			const names = installations
				.map((installation) => installation.name)
				.sort()
				.join("\n");
			const isChanged = installationNames.current !== null && installationNames.current !== names;
			installationNames.current = names;
			if (isCallback || isReloadPending.current || (isAddingAccount.current && isChanged)) {
				setReloadKey((key) => key + 1);
			}
			isReloadPending.current = false;
			setOptions(
				installations.map((installation) => ({
					value: installation.name,
					label: installation.name,
					...installation,
				})),
			);
			setIsLoading(false);
		});
	};

	const addAccount = () => {
		isAddingAccount.current = true;
		isReloadPending.current = true;
		void createChildWindow(
			isRelease
				? "https://github.com/apps/gram-ax/installations/select_target"
				: "https://github.com/apps/gramax-dev/installations/select_target",
			700,
			550,
			"https://github.com/login/device/success",
			() => loadInstallations(true),
		);
	};

	const onOpenChange = (open: boolean) => {
		if (!open || (isLoaded.current && !isAddingAccount.current)) return;
		isLoaded.current = true;
		loadInstallations();
	};

	return { options, isLoading, onOpenChange, addAccount, reloadKey };
};

export default useGithubInstallations;
